import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { createProject } from './api';
import { request, buildQuery } from '../../lib/apiClient';
import { useIsMobile } from '../../hooks/useIsMobile';
const INITIAL_FORM = {
    title: '',
    description: '',
    instructions: '',
    customerId: '',
    customerName: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    scheduledDate: '',
    scheduledStart: '',
    scheduledEnd: '',
    priority: 'normal',
};
export const ProjectCreate = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const [form, setForm] = useState(INITIAL_FORM);
    const [fieldErrors, setFieldErrors] = useState({});
    const [submitError, setSubmitError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Customer search state
    const [customerSearch, setCustomerSearch] = useState('');
    const [customerSuggestions, setCustomerSuggestions] = useState([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const customerRef = useRef(null);
    const debounceRef = useRef(null);
    const set = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
        setFieldErrors((prev) => ({ ...prev, [field]: undefined }));
    };
    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (customerRef.current && !customerRef.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    // Debounced customer search
    const searchCustomers = useCallback(async (query) => {
        if (!query.trim()) {
            setCustomerSuggestions([]);
            setShowDropdown(false);
            return;
        }
        setCustomerLoading(true);
        try {
            const qs = buildQuery({ name: query, limit: 10 });
            const data = await request(`/customers${qs}`);
            setCustomerSuggestions(data.items);
            setShowDropdown(true);
        }
        catch {
            // silently ignore search errors
        }
        finally {
            setCustomerLoading(false);
        }
    }, []);
    const handleCustomerSearchChange = (e) => {
        const val = e.target.value;
        setCustomerSearch(val);
        if (debounceRef.current)
            clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => searchCustomers(val), 300);
    };
    const selectCustomer = (customer) => {
        setSelectedCustomer(customer);
        setForm((prev) => ({
            ...prev,
            customerId: customer.id,
            customerName: customer.name,
            street: customer.addresses[0]?.street ?? '',
            city: customer.addresses[0]?.city ?? '',
            state: customer.addresses[0]?.state ?? '',
            zip: customer.addresses[0]?.zip ?? '',
        }));
        setFieldErrors((prev) => ({
            ...prev,
            customerId: undefined,
            customerName: undefined,
            street: undefined,
            city: undefined,
            state: undefined,
            zip: undefined,
        }));
        setCustomerSearch('');
        setCustomerSuggestions([]);
        setShowDropdown(false);
    };
    const clearCustomer = () => {
        setSelectedCustomer(null);
        setForm((prev) => ({
            ...prev,
            customerId: '',
            customerName: '',
            street: '',
            city: '',
            state: '',
            zip: '',
        }));
    };
    const validate = () => {
        const errors = {};
        if (!form.title.trim())
            errors.title = 'Title is required.';
        if (!form.customerId.trim()) {
            errors.customerId = 'Customer ID is required.';
        }
        else if (!/^CUST-\d{5}$/.test(form.customerId.trim())) {
            errors.customerId = 'Must match format CUST-00001.';
        }
        if (!form.customerName.trim())
            errors.customerName = 'Customer name is required.';
        if (!form.street.trim())
            errors.street = 'Street is required.';
        if (!form.city.trim())
            errors.city = 'City is required.';
        if (!form.state.trim())
            errors.state = 'State is required.';
        else if (form.state.trim().length !== 2)
            errors.state = 'Use 2-letter state code.';
        if (!form.zip.trim())
            errors.zip = 'ZIP is required.';
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate())
            return;
        setSubmitting(true);
        setSubmitError(null);
        try {
            const project = await createProject({
                title: form.title.trim(),
                description: form.description.trim(),
                instructions: form.instructions.trim(),
                customerId: form.customerId.trim(),
                customerName: form.customerName.trim(),
                scheduledDate: form.scheduledDate || undefined,
                scheduledStart: form.scheduledStart || undefined,
                scheduledEnd: form.scheduledEnd || undefined,
                priority: form.priority,
                siteAddress: {
                    street: form.street.trim(),
                    city: form.city.trim(),
                    state: form.state.trim().toUpperCase(),
                    zip: form.zip.trim(),
                },
            });
            navigate(`/projects/${project.id}`);
        }
        catch (e) {
            // Try to parse 422 errors array
            let message = e.message ?? 'Failed to create project';
            try {
                const parsed = JSON.parse(e.message);
                if (Array.isArray(parsed.errors)) {
                    message = parsed.errors.map((err) => err.message ?? err).join(', ');
                }
                else if (parsed.error) {
                    message = parsed.error;
                }
            }
            catch {
                // message stays as-is
            }
            setSubmitError(message);
        }
        finally {
            setSubmitting(false);
        }
    };
    const inputStyle = {
        ...styles.input,
        fontSize: isMobile ? '16px' : '15px',
    };
    const textareaStyle = {
        ...styles.textarea,
        fontSize: isMobile ? '16px' : '15px',
    };
    const cardStyle = {
        ...styles.card,
        padding: isMobile ? '20px 16px' : '28px 32px',
    };
    const twoColStyle = {
        ...styles.twoCol,
        gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    };
    const threeColStyle = {
        ...styles.threeCol,
        gridTemplateColumns: isMobile ? '1fr' : '1fr 80px 110px',
    };
    return (_jsx(Layout, { children: _jsxs("div", { style: { maxWidth: '680px' }, children: [_jsx("button", { style: styles.backLink, onClick: () => navigate('/projects'), children: "\u2190 Projects" }), _jsxs("div", { style: styles.pageHeader, children: [_jsx("h2", { style: styles.heading, children: "New Project" }), _jsx("p", { style: styles.subheading, children: "Create a new field service project" })] }), _jsxs("form", { onSubmit: handleSubmit, noValidate: true, children: [_jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Basic Info" }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["Title ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                ...inputStyle,
                                                ...(fieldErrors.title ? styles.inputError : {}),
                                            }, type: "text", value: form.title, onChange: set('title'), placeholder: "e.g. Window installation \u2013 Unit 4B" }), fieldErrors.title && _jsx("p", { style: styles.fieldError, children: fieldErrors.title })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Description" }), _jsx("textarea", { style: textareaStyle, rows: 3, value: form.description, onChange: set('description'), placeholder: "Brief summary of the project\u2026" })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Instructions" }), _jsx("textarea", { style: textareaStyle, rows: 5, value: form.instructions, onChange: set('instructions'), placeholder: "Detailed job instructions for the installer\u2026" })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Customer" }), _jsxs("div", { style: styles.formGroup, ref: customerRef, children: [_jsxs("label", { style: styles.label, children: ["Customer ", _jsx("span", { style: styles.required, children: "*" })] }), selectedCustomer ? (_jsxs("div", { style: styles.selectedCustomerChip, children: [_jsxs("div", { style: styles.chipInfo, children: [_jsx("span", { style: styles.chipName, children: selectedCustomer.name }), _jsx("span", { style: styles.chipId, children: selectedCustomer.id })] }), _jsx("button", { type: "button", style: styles.chipClear, onClick: clearCustomer, "aria-label": "Clear customer", children: "\u00D7" })] })) : (_jsxs("div", { style: { position: 'relative' }, children: [_jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.customerId || fieldErrors.customerName ? styles.inputError : {}),
                                                    }, type: "text", value: customerSearch, onChange: handleCustomerSearchChange, onFocus: () => customerSuggestions.length > 0 && setShowDropdown(true), placeholder: "Search by customer name\u2026", autoComplete: "off" }), customerLoading && (_jsx("span", { style: styles.searchSpinner })), showDropdown && customerSuggestions.length > 0 && (_jsx("div", { style: styles.dropdown, children: customerSuggestions.map((c) => (_jsxs("button", { type: "button", style: styles.dropdownItem, onMouseDown: () => selectCustomer(c), children: [_jsx("span", { style: styles.dropdownName, children: c.name }), _jsxs("span", { style: styles.dropdownMeta, children: [c.id, c.addresses[0] && ` · ${c.addresses[0].city}, ${c.addresses[0].state}`] })] }, c.id))) })), showDropdown && !customerLoading && customerSuggestions.length === 0 && customerSearch.trim() && (_jsx("div", { style: styles.dropdown, children: _jsx("p", { style: styles.dropdownEmpty, children: "No customers found." }) }))] })), (fieldErrors.customerId || fieldErrors.customerName) && (_jsx("p", { style: styles.fieldError, children: fieldErrors.customerId ?? fieldErrors.customerName }))] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Site Address" }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["Street ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                ...inputStyle,
                                                ...(fieldErrors.street ? styles.inputError : {}),
                                            }, type: "text", value: form.street, onChange: set('street'), placeholder: "123 Main St" }), fieldErrors.street && _jsx("p", { style: styles.fieldError, children: fieldErrors.street })] }), _jsxs("div", { style: threeColStyle, children: [_jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["City ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.city ? styles.inputError : {}),
                                                    }, type: "text", value: form.city, onChange: set('city'), placeholder: "Springfield" }), fieldErrors.city && _jsx("p", { style: styles.fieldError, children: fieldErrors.city })] }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["State ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.state ? styles.inputError : {}),
                                                    }, type: "text", value: form.state, onChange: set('state'), placeholder: "IL", maxLength: 2 }), fieldErrors.state && _jsx("p", { style: styles.fieldError, children: fieldErrors.state })] }), _jsxs("div", { style: styles.formGroup, children: [_jsxs("label", { style: styles.label, children: ["ZIP ", _jsx("span", { style: styles.required, children: "*" })] }), _jsx("input", { style: {
                                                        ...inputStyle,
                                                        ...(fieldErrors.zip ? styles.inputError : {}),
                                                    }, type: "text", value: form.zip, onChange: set('zip'), placeholder: "62701" }), fieldErrors.zip && _jsx("p", { style: styles.fieldError, children: fieldErrors.zip })] })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("h3", { style: styles.sectionTitle, children: "Schedule & Priority" }), _jsxs("div", { style: twoColStyle, children: [_jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Scheduled Date" }), _jsx("input", { style: inputStyle, type: "date", value: form.scheduledDate, onChange: set('scheduledDate') })] }), _jsx("div", {}), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Scheduled Start" }), _jsx("input", { style: inputStyle, type: "time", value: form.scheduledStart, onChange: set('scheduledStart') })] }), _jsxs("div", { style: styles.formGroup, children: [_jsx("label", { style: styles.label, children: "Scheduled End" }), _jsx("input", { style: inputStyle, type: "time", value: form.scheduledEnd, onChange: set('scheduledEnd') })] })] }), _jsxs("div", { style: { ...styles.formGroup, marginTop: '8px' }, children: [_jsx("label", { style: styles.label, children: "Priority" }), _jsxs("div", { style: styles.radioGroup, children: [_jsxs("label", { style: styles.radioLabel, children: [_jsx("input", { type: "radio", name: "priority", value: "normal", checked: form.priority === 'normal', onChange: () => setForm((p) => ({ ...p, priority: 'normal' })), style: { marginRight: '6px' } }), "Normal"] }), _jsxs("label", { style: styles.radioLabel, children: [_jsx("input", { type: "radio", name: "priority", value: "urgent", checked: form.priority === 'urgent', onChange: () => setForm((p) => ({ ...p, priority: 'urgent' })), style: { marginRight: '6px' } }), "Urgent"] })] })] })] }), submitError && _jsx("div", { style: styles.errorBanner, children: submitError }), _jsxs("div", { style: styles.actions, children: [_jsx("button", { type: "button", style: styles.ghostButton, onClick: () => navigate('/projects'), disabled: submitting, children: "Cancel" }), _jsx("button", { type: "submit", style: styles.primaryButton, disabled: submitting, children: submitting ? 'Creating…' : 'Create Project' })] })] })] }) }));
};
const styles = {
    backLink: {
        background: 'none',
        border: 'none',
        color: '#0071e3',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        padding: '0 0 20px 0',
        display: 'block',
    },
    pageHeader: {
        marginBottom: '24px',
    },
    heading: {
        fontSize: '42px',
        fontWeight: '700',
        margin: '0 0 8px 0',
        color: '#1d1d1f',
        letterSpacing: '-0.5px',
    },
    subheading: {
        fontSize: '17px',
        fontWeight: '400',
        margin: 0,
        color: '#86868b',
    },
    card: {
        padding: '28px 32px',
        backgroundColor: 'rgba(255,255,255,0.5)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.8)',
        borderRadius: '20px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '20px',
    },
    sectionTitle: {
        fontSize: '18px',
        fontWeight: '700',
        color: '#1d1d1f',
        margin: '0 0 20px 0',
        letterSpacing: '-0.3px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginBottom: '16px',
    },
    label: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    required: {
        color: '#ff3b30',
    },
    input: {
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    inputError: {
        borderColor: 'rgba(255,59,48,0.6)',
        backgroundColor: 'rgba(255,59,48,0.04)',
    },
    textarea: {
        padding: '10px 14px',
        fontSize: '15px',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '10px',
        backgroundColor: 'rgba(255,255,255,0.8)',
        color: '#1d1d1f',
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
        width: '100%',
        boxSizing: 'border-box',
    },
    fieldError: {
        margin: '2px 0 0 0',
        fontSize: '12px',
        color: '#c0392b',
        fontWeight: '500',
    },
    twoCol: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '0 20px',
    },
    threeCol: {
        display: 'grid',
        gridTemplateColumns: '1fr 80px 110px',
        gap: '0 12px',
    },
    radioGroup: {
        display: 'flex',
        gap: '24px',
        alignItems: 'center',
    },
    radioLabel: {
        fontSize: '15px',
        color: '#1d1d1f',
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '8px',
        marginBottom: '40px',
    },
    primaryButton: {
        padding: '12px 28px',
        backgroundColor: '#0071e3',
        color: '#fff',
        border: 'none',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        minHeight: '44px',
    },
    ghostButton: {
        padding: '12px 28px',
        backgroundColor: 'transparent',
        color: '#86868b',
        border: '1px solid rgba(0,0,0,0.15)',
        borderRadius: '980px',
        fontSize: '15px',
        fontWeight: '500',
        cursor: 'pointer',
        minHeight: '44px',
    },
    errorBanner: {
        padding: '14px 18px',
        backgroundColor: 'rgba(255,59,48,0.1)',
        border: '1px solid rgba(255,59,48,0.3)',
        borderRadius: '12px',
        color: '#c0392b',
        fontSize: '14px',
        marginBottom: '16px',
    },
    selectedCustomerChip: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        backgroundColor: 'rgba(0,113,227,0.06)',
        border: '1px solid rgba(0,113,227,0.25)',
        borderRadius: '10px',
    },
    chipInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    chipName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    chipId: {
        fontSize: '12px',
        color: '#86868b',
        fontWeight: '500',
    },
    chipClear: {
        background: 'none',
        border: 'none',
        fontSize: '20px',
        lineHeight: '1',
        color: '#86868b',
        cursor: 'pointer',
        padding: '0 4px',
    },
    searchSpinner: {
        position: 'absolute',
        right: '12px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '16px',
        height: '16px',
        border: '2px solid rgba(0,0,0,0.1)',
        borderTopColor: '#0071e3',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        display: 'inline-block',
    },
    dropdown: {
        position: 'absolute',
        top: 'calc(100% + 4px)',
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255,255,255,0.98)',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '12px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        zIndex: 100,
        overflow: 'hidden',
        maxHeight: '260px',
        overflowY: 'auto',
    },
    dropdownItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '2px',
        width: '100%',
        padding: '10px 14px',
        background: 'none',
        border: 'none',
        borderBottom: '1px solid rgba(0,0,0,0.06)',
        cursor: 'pointer',
        textAlign: 'left',
    },
    dropdownName: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1d1d1f',
    },
    dropdownMeta: {
        fontSize: '12px',
        color: '#86868b',
        fontWeight: '500',
    },
    dropdownEmpty: {
        padding: '14px',
        margin: 0,
        fontSize: '14px',
        color: '#86868b',
        textAlign: 'center',
    },
};
//# sourceMappingURL=ProjectCreate.js.map